import React, { useState } from 'react';
import { PostResponseSchema } from '@api/client';
import LoadingSpinner from '../../components/LoadingSpinner';
import TitleOverlay from '../../components/TitleOverlay';
import PostCardAudioPlayer from './PostCardAudioPlayer';

interface Props {
  post: PostResponseSchema;
  onVideoClick?: (post: PostResponseSchema) => void;
  onImageClick?: (post: PostResponseSchema) => void;
  wrapperStyle?: React.CSSProperties;
}

export default function PostCardContent({
  post,
  onVideoClick,
  onImageClick,
  wrapperStyle
}: Props): React.ReactElement | null {
  const [imageLoading, setImageLoading] = useState(true);

  // Audio posts
  if (post.audio_url) {
    return <PostCardAudioPlayer post={post} />;
  }

  // Video posts (prioritize video over photo)
  if (post.m3u8_url) {
    return (
      <div
        className="video-wrapper"
        onClick={() => onVideoClick?.(post)}
        style={{ pointerEvents: 'auto', ...wrapperStyle }}
      >
        {imageLoading && <LoadingSpinner />}

        <img
          src={post.photo_url || ''}
          alt="Video preview"
          className="post-image"
          style={{
            cursor: 'pointer',
            pointerEvents: 'none',
            opacity: imageLoading ? 0 : 1,
            transition: 'opacity 0.3s ease'
          }}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />

        <TitleOverlay title={post.title} messageText={post.message_text} />
      </div>
    );
  }

  // Photo posts
  if (post.photo_url) {
    return (
      <div
        className="image-wrapper"
        onClick={() => onImageClick?.(post)}
        style={{ pointerEvents: 'auto', ...wrapperStyle }}
      >
        {imageLoading && <LoadingSpinner />}

        <img
          src={post.photo_url}
          alt="Post content"
          className="post-image"
          style={{
            cursor: 'pointer',
            pointerEvents: 'none',
            opacity: imageLoading ? 0 : 1,
            transition: 'opacity 0.3s ease'
          }}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />

        <TitleOverlay title={post.title} messageText={post.message_text} />
      </div>
    );
  }

  // Text-only posts
  if (post.message_text || post.title) {
    return (
      <div className="text-only-wrapper">
        <div className="text-only-content">
          {post.title && <h2 className="text-only-title">{post.title}</h2>}
          {post.message_text && <p className="text-only-message">{post.message_text}</p>}
        </div>
      </div>
    );
  }

  return null;
}
